"use client";

// Tremor Select [v1.0.0]
import * as SelectPrimitives from "@radix-ui/react-select";

import React from "react";
import {
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiCheckLine,
  RiExpandUpDownLine,
} from "react-icons/ri";

import { cx, focusInput, hasErrorInput } from "@/lib/tailwind-utils";

/** Lets a trigger open its own list; see `SelectTrigger` for why. */
const SelectOpenContext = React.createContext<((open: boolean) => void) | null>(
  null,
);

const Select = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof SelectPrimitives.Root>) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  );
  const open = openProp ?? uncontrolledOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (openProp === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [openProp, onOpenChange],
  );

  return (
    <SelectOpenContext.Provider value={setOpen}>
      <SelectPrimitives.Root open={open} onOpenChange={setOpen} {...props} />
    </SelectOpenContext.Provider>
  );
};
Select.displayName = "Select";

/**
 * How far a finger may drift during a tap before it counts as a scroll. Generous,
 * as iPad shrinks the desktop site to fit and a finger's wobble grows with it.
 */
const TAP_SLOP_PX = 24;

/**
 * A tap from a finger or pen. iPad Safari asks for the desktop site by default and
 * then passes off taps as a mouse ("MacIntel" with touch points), so those count too.
 */
function isTouchPress(event: React.PointerEvent): boolean {
  // Only a plain primary press; right- and Ctrl-clicks keep their usual meaning.
  if (event.button !== 0 || event.ctrlKey) return false;
  if (event.pointerType === "touch" || event.pointerType === "pen") return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

const SelectGroup = SelectPrimitives.Group;
SelectGroup.displayName = "SelectGroup";

const SelectValue = SelectPrimitives.Value;
SelectValue.displayName = "SelectValue";

const selectTriggerStyles = [
  cx(
    // base
    "group/trigger flex w-full select-none items-center justify-between gap-2 truncate rounded-xl border px-3 py-2 shadow-sm outline-hidden transition",
    "text-body",
    // text color
    "text-foreground",
    // placeholder
    "data-[placeholder]:text-foreground-muted",
    // hover
    "hover:bg-row-hover",
    // disabled
    "data-disabled:bg-muted data-disabled:text-foreground-muted",
    focusInput,
  ),
];

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Trigger> & {
    hasError?: boolean;
    errorMessage?: string;
    /** Wraps a long selected value onto more lines instead of cutting it off. */
    wrapText?: boolean;
  }
>(
  (
    {
      className,
      hasError,
      errorMessage,
      wrapText,
      children,
      disabled,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
      onClick,
      ...props
    },
    forwardedRef,
  ) => {
    // Radix opens on press for a mouse and on click for touch. On an iPad that reports
    // taps as a mouse, it opens on press and then shuts again when the finger drifts
    // before lifting, so the list never shows. Taps are opened here on lift instead,
    // and Radix's own press and click handling is skipped for them.
    const setOpen = React.useContext(SelectOpenContext);
    const tapStart = React.useRef<{ x: number; y: number; id: number } | null>(
      null,
    );
    const isTap = React.useRef(false);

    return (
      <>
        <SelectPrimitives.Trigger
          ref={forwardedRef}
          onPointerDown={(event) => {
            onPointerDown?.(event);
            isTap.current = false;
            if (event.defaultPrevented || !setOpen || !isTouchPress(event)) {
              return;
            }
            isTap.current = true;
            tapStart.current = {
              x: event.clientX,
              y: event.clientY,
              id: event.pointerId,
            };
            event.preventDefault();
          }}
          onPointerUp={(event) => {
            onPointerUp?.(event);
            const start = tapStart.current;
            tapStart.current = null;
            if (!start || start.id !== event.pointerId || !setOpen) return;
            if (event.currentTarget.disabled) return;
            const drift = Math.hypot(
              event.clientX - start.x,
              event.clientY - start.y,
            );
            if (drift > TAP_SLOP_PX) return;
            event.currentTarget.focus();
            setOpen(true);
          }}
          onPointerCancel={(event) => {
            onPointerCancel?.(event);
            tapStart.current = null;
          }}
          onClick={(event) => {
            onClick?.(event);
            if (isTap.current) {
              isTap.current = false;
              event.preventDefault();
            }
          }}
          className={cx(
            selectTriggerStyles,
            hasError ? hasErrorInput : "",
            disabled && "!opacity-70",
            wrapText && "text-left",
            className,
          )}
          disabled={disabled}
          tremor-id="tremor-raw"
          {...props}
        >
          <span
            className={
              wrapText ? "min-w-0 whitespace-normal break-words" : "truncate"
            }
          >
            {children}
          </span>
          <SelectPrimitives.Icon asChild>
            <RiExpandUpDownLine
              className={cx(
                // base
                "size-4 shrink-0",
                // text color
                "text-foreground-muted",
                // disabled
                "group-data-disabled/trigger:text-foreground-muted",
              )}
            />
          </SelectPrimitives.Icon>
        </SelectPrimitives.Trigger>
        {hasError && errorMessage && (
          <p className="mt-1.5 text-caption text-status-error-text">
            {errorMessage}
          </p>
        )}
      </>
    );
  },
);

SelectTrigger.displayName = "SelectTrigger";

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollUpButton>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.ScrollUpButton
    ref={forwardedRef}
    className={cx(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <RiArrowUpSLine className="size-3 shrink-0" aria-hidden="true" />
  </SelectPrimitives.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitives.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollDownButton>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.ScrollDownButton
    ref={forwardedRef}
    className={cx(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <RiArrowDownSLine className="size-3 shrink-0" aria-hidden="true" />
  </SelectPrimitives.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitives.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Content> & {
    /** Keeps the list near its trigger's width so long options wrap. */
    wrapText?: boolean;
  }
>(
  (
    {
      className,
      wrapText,
      position = "popper",
      children,
      sideOffset = 8,
      collisionPadding = 10,
      ...props
    },
    forwardedRef,
  ) => (
    <SelectPrimitives.Portal>
      <SelectPrimitives.Content
        ref={forwardedRef}
        className={cx(
          "text-body",
          // base
          "relative z-50 overflow-hidden rounded-xl border shadow-xl shadow-black/[2.5%]",
          // widths
          "min-w-[calc(var(--radix-select-trigger-width)-2px)] max-w-[95vw]",
          wrapText &&
            "max-w-[min(95vw,max(var(--radix-select-trigger-width),20rem))]",
          // heights
          "max-h-[min(var(--radix-select-content-available-height),_300px)]",
          // background color
          "bg-background/90 backdrop-blur-sm",
          // text color
          "text-foreground",
          // border color
          "border-border",
          // transition
          "will-change-[transform,opacity]",
          "data-[state=closed]:animate-hide",
          "data-[side=bottom]:animate-slide-down-and-fade data-[side=left]:animate-slide-left-and-fade data-[side=right]:animate-slide-right-and-fade data-[side=top]:animate-slide-up-and-fade",
          className,
        )}
        sideOffset={sideOffset}
        position={position}
        collisionPadding={collisionPadding}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitives.Viewport
          className={cx(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[calc(var(--radix-select-trigger-width))]",
          )}
        >
          {children}
        </SelectPrimitives.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitives.Content>
    </SelectPrimitives.Portal>
  ),
);

SelectContent.displayName = "SelectContent";

const SelectGroupLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Label>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.Label
    ref={forwardedRef}
    className={cx(
      // base
      "px-3 py-2 text-caption-strong tracking-wide",
      // text color
      "text-muted-foreground",
      className,
    )}
    {...props}
  />
));

SelectGroupLabel.displayName = "SelectGroupLabel";

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Item> & {
    showCheckmark?: boolean;
    description?: string;
  }
>(
  (
    { className, children, showCheckmark = true, description, ...props },
    forwardedRef,
  ) => {
    return (
      <SelectPrimitives.Item
        ref={forwardedRef}
        className={cx(
          // base
          "group/item outline-hidden grid cursor-pointer gap-x-2 rounded-xl px-3 py-2 transition-colors data-[state=checked]:font-semibold sm:text-body",
          // text color
          "text-foreground",
          // disabled
          "data-[disabled]:!cursor-not-allowed data-[disabled]:!text-foreground-muted data-[disabled]:!bg-transparent",
          // focus
          "focus-visible:bg-hover",
          // hover
          "hover:bg-hover hover:outline-none",
          className,
          showCheckmark && "grid-cols-[minmax(0,1fr)_20px] gap-x-2",
        )}
        {...props}
      >
        <div className="flex min-w-0 flex-col">
          <SelectPrimitives.ItemText className="break-words">
            {children}
          </SelectPrimitives.ItemText>
          {description && (
            <span className="text-caption text-foreground-muted group-data-[disabled]/item:!opacity-50">
              {description}
            </span>
          )}
        </div>
        {showCheckmark && (
          <SelectPrimitives.ItemIndicator>
            <RiCheckLine
              className="size-5 shrink-0 text-foreground"
              aria-hidden="true"
            />
          </SelectPrimitives.ItemIndicator>
        )}
      </SelectPrimitives.Item>
    );
  },
);

SelectItem.displayName = "SelectItem";

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitives.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitives.Separator>
>(({ className, ...props }, forwardedRef) => (
  <SelectPrimitives.Separator
    ref={forwardedRef}
    className={cx(
      // base
      "-mx-1 my-1 h-px",
      // background color
      "bg-border-secondary",
      className,
    )}
    {...props}
  />
));

SelectSeparator.displayName = "SelectSeparator";

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};

export {
  DynamicSelectItem,
  SearchSelect,
  SearchSelectContent,
  SearchSelectItem,
  SearchSelectTrigger,
  SearchSelectValue,
  useSearchSelect,
} from "./search-select";
